/**
 * HowToPlay — Foldable step-by-step instructions & rules panel
 *
 * Sits at the bottom of every game. Collapsed by default.
 * Click to expand and see:
 * - Step-by-step "How to Play" instructions
 * - Game rules
 * - Tips (optional)
 *
 * Each game provides its own content via the gameInstructions data file.
 */

import React, { useState } from 'react';
import './HowToPlay.css';

export interface GameInstructions {
  gameName: string;
  steps: string[];
  rules: string[];
  tips?: string[];
}

interface HowToPlayProps {
  instructions: GameInstructions;
  accentColor?: string;
}

export const HowToPlay: React.FC<HowToPlayProps> = ({
  instructions,
  accentColor = '#6366f1',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'steps' | 'rules' | 'tips'>('steps');

  return (
    <div className={`how-to-play ${isOpen ? 'how-to-play--open' : ''}`}>
      <button
        className="how-to-play__toggle"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-controls="how-to-play-content"
        style={{ borderColor: isOpen ? accentColor : undefined }}
      >
        <span className="how-to-play__toggle-icon">{isOpen ? '▼' : '▶'}</span>
        <span className="how-to-play__toggle-text">
          📖 How to Play {instructions.gameName}
        </span>
      </button>

      {isOpen && (
        <div className="how-to-play__content" id="how-to-play-content">
          <div className="how-to-play__tabs">
            <button
              className={`how-to-play__tab ${activeTab === 'steps' ? 'active' : ''}`}
              onClick={() => setActiveTab('steps')}
              style={activeTab === 'steps' ? { borderBottomColor: accentColor } : {}}
            >
              🎯 Steps
            </button>
            <button
              className={`how-to-play__tab ${activeTab === 'rules' ? 'active' : ''}`}
              onClick={() => setActiveTab('rules')}
              style={activeTab === 'rules' ? { borderBottomColor: accentColor } : {}}
            >
              📜 Rules
            </button>
            {instructions.tips && instructions.tips.length > 0 && (
              <button
                className={`how-to-play__tab ${activeTab === 'tips' ? 'active' : ''}`}
                onClick={() => setActiveTab('tips')}
                style={activeTab === 'tips' ? { borderBottomColor: accentColor } : {}}
              >
                💡 Tips
              </button>
            )}
          </div>

          <div className="how-to-play__body">
            {activeTab === 'steps' && (
              <ol className="how-to-play__steps">
                {instructions.steps.map((step, i) => (
                  <li key={i}>
                    <span className="how-to-play__step-num" style={{ background: accentColor }}>{i + 1}</span>
                    <span className="how-to-play__step-text">{step}</span>
                  </li>
                ))}
              </ol>
            )}

            {activeTab === 'rules' && (
              <ul className="how-to-play__rules">
                {instructions.rules.map((rule, i) => (
                  <li key={i}>{rule}</li>
                ))}
              </ul>
            )}

            {activeTab === 'tips' && instructions.tips && (
              <ul className="how-to-play__tips">
                {instructions.tips.map((tip, i) => (
                  <li key={i}>{tip}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HowToPlay;
